/**
 * Environment-neutral Retro Store product metadata helpers.
 *
 * This module deliberately avoids DOMParser, React, browser globals, and path
 * aliases so it can be consumed by both the SPA bundle and Cloudflare Pages
 * Functions. Tebex's editor may wrap JSON in arbitrary HTML; extraction is
 * therefore text based and remains compatible with the original retroMeta
 * shape while exposing a stable v2 contract.
 */

export const RETRO_META_SCHEMA_VERSION = 2;
export const DEFAULT_BACKEND_URL = 'https://api.retrostore.dev';
export const TEBEX_IMAGE_WIDTHS = Object.freeze([320, 480, 640, 960, 1280, 1600, 1920]);

const TEBEX_MEDIA_HOST = 'dunb17ur4ymx4.cloudfront.net';

const isObject = value => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const ensureArray = (value) => {
  if (value === null || value === undefined || value === '') return [];
  return (Array.isArray(value) ? value : [value]).filter(Boolean);
};

const uniqueStrings = (values) => {
  const seen = new Set();
  return ensureArray(values).reduce((result, value) => {
    const normalized = String(value).trim();
    const key = normalized.toLowerCase();
    if (normalized && !seen.has(key)) {
      seen.add(key);
      result.push(normalized);
    }
    return result;
  }, []);
};

const booleanValue = (value, fallback = false) => {
  if (value === true || value === 1 || String(value).toLowerCase() === 'true') return true;
  if (value === false || value === 0 || String(value).toLowerCase() === 'false') return false;
  return fallback;
};

export const slugifyProductName = (value) => {
  if (!value) return '';
  return String(value)
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

export const extractYouTubeId = (input) => {
  if (!input) return null;
  const value = String(input).trim();
  const patterns = [
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?(?:[^#\s]*&)?v=([a-zA-Z0-9_-]{11})/,
    /(?:https?:\/\/)?(?:www\.)?youtu\.be\/([a-zA-Z0-9_-]{11})/,
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/(?:embed|v|shorts)\/([a-zA-Z0-9_-]{11})/,
    /(?:https?:\/\/)?(?:www\.)?youtube-nocookie\.com\/embed\/([a-zA-Z0-9_-]{11})/
  ];

  for (const pattern of patterns) {
    const match = value.match(pattern);
    if (match) return match[1];
  }

  return /^[a-zA-Z0-9_-]{11}$/.test(value) ? value : null;
};

export const decodeHtmlEntities = (input) => {
  if (input === null || input === undefined) return input;
  const named = {
    amp: '&',
    apos: "'",
    gt: '>',
    lt: '<',
    nbsp: ' ',
    quot: '"'
  };

  let decoded = String(input);
  // Tebex can encode an already encoded attribute, so decode a few bounded
  // passes rather than relying on a browser-only textarea/DOMParser trick.
  for (let pass = 0; pass < 3; pass += 1) {
    const next = decoded.replace(/&(?:#(\d+)|#x([\da-f]+)|([a-z]+));/gi, (match, decimal, hex, entity) => {
      if (decimal) return String.fromCodePoint(Number(decimal));
      if (hex) return String.fromCodePoint(parseInt(hex, 16));
      return Object.prototype.hasOwnProperty.call(named, entity.toLowerCase())
        ? named[entity.toLowerCase()]
        : match;
    });
    if (next === decoded) break;
    decoded = next;
  }
  return decoded;
};

export const stripHtml = (html) => {
  if (!html) return '';
  const withoutMarkup = String(html)
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(?:p|div|pre|code|li|ol|ul|section|article|h[1-6])\s*>/gi, '\n')
    .replace(/<[^>]*>/g, ' ');

  return decodeHtmlEntities(withoutMarkup)
    .replace(/<[^>]*>/g, ' ')
    .replace(/\u00a0/g, ' ')
    .replace(/[\t\f\v]+/g, ' ')
    .replace(/ *\n */g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

export const extractImagesFromDescription = (description) => {
  if (!description) return [];
  const images = [];
  const pattern = /(?:src|href)=["']([^"']+)["']/gi;
  let match;
  while ((match = pattern.exec(String(description))) !== null) {
    const url = decodeHtmlEntities(match[1]);
    if (url.includes(TEBEX_MEDIA_HOST) && !images.includes(url)) images.push(url);
  }
  return images;
};

const isEscaped = (value, index) => {
  let slashes = 0;
  for (let cursor = index - 1; cursor >= 0 && value[cursor] === '\\'; cursor -= 1) slashes += 1;
  return slashes % 2 === 1;
};

/** Repair the common Tebex-editor mutation where quotes inside copy are no
 * longer JSON escaped (for example Terrence "Lowkey" Shaw). */
const repairUnescapedQuotes = (input) => {
  const result = [];
  let inString = false;

  for (let index = 0; index < input.length; index += 1) {
    const character = input[index];
    if (inString && (character === '\n' || character === '\r' || character === '\t')) {
      if (character === '\r' && input[index + 1] === '\n') continue;
      const escapedWhitespace = character === '\t' ? '\\t' : '\\n';
      if (result[result.length - 1] !== escapedWhitespace) result.push(escapedWhitespace);
      continue;
    }
    if (character !== '"' || isEscaped(input, index)) {
      result.push(character);
      continue;
    }

    if (!inString) {
      inString = true;
      result.push(character);
      continue;
    }

    let cursor = index + 1;
    while (cursor < input.length && /\s/.test(input[cursor])) cursor += 1;
    const next = input[cursor];
    const closesJsonString = next === ':' || next === ',' || next === '}' || next === ']' || next === undefined;

    if (closesJsonString) {
      inString = false;
      result.push(character);
    } else {
      result.push('\\"');
    }
  }

  return result.join('');
};

const parseJsonCandidate = (input) => {
  if (!input || typeof input !== 'string') return null;
  let candidate = decodeHtmlEntities(input)
    .replace(/^\uFEFF/, '')
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[\u2018\u2019]/g, "'")
    .trim();

  if (!candidate) return null;
  if (!candidate.startsWith('{') && /^\s*["']retroMeta["']\s*:/.test(candidate)) {
    candidate = `{${candidate}}`;
  }

  const attempts = [candidate, repairUnescapedQuotes(candidate)];
  for (const attempt of attempts) {
    try {
      return JSON.parse(attempt);
    } catch (error) {
      // Continue to the next bounded repair strategy.
    }
  }
  return null;
};

const unwrapRetroMeta = (value) => {
  if (!isObject(value)) return null;
  return isObject(value.retroMeta) ? value.retroMeta : value;
};

const jsonObjectCandidate = (input) => {
  if (!input) return null;
  const value = String(input).trim();
  const markerIndex = value.search(/["']retroMeta["']\s*:/i);
  if (markerIndex < 0) return value.startsWith('{') && value.endsWith('}') ? value : null;

  const firstBrace = value.lastIndexOf('{', markerIndex);
  const lastBrace = value.lastIndexOf('}');
  if (firstBrace >= 0 && lastBrace > firstBrace) return value.slice(firstBrace, lastBrace + 1);

  const propertyStart = value.lastIndexOf('"', markerIndex);
  return propertyStart >= 0 ? `{${value.slice(propertyStart, lastBrace >= 0 ? lastBrace + 1 : undefined)}}` : null;
};

/**
 * Extract the un-normalized retroMeta payload from pure JSON or Tebex-mutated
 * HTML. The ordered candidates cover data attributes, dedicated blocks,
 * comments/code fences, and regular editor paragraph/line-break mutations.
 */
export const extractRetroMeta = (description) => {
  if (isObject(description)) return unwrapRetroMeta(description);
  if (!description) return null;

  const source = String(description);
  const candidates = [source];

  const dataAttribute = source.match(/data-retro-meta\s*=\s*(["'])([\s\S]*?)\1/i);
  if (dataAttribute) candidates.push(dataAttribute[2]);

  const dedicatedBlock = source.match(/<(?:pre|code|script|div)[^>]*(?:class|id)=["'][^"']*retro-meta[^"']*["'][^>]*>([\s\S]*?)<\/(?:pre|code|script|div)>/i);
  if (dedicatedBlock) candidates.push(dedicatedBlock[1]);

  const fenced = source.match(/```(?:json)?\s*([\s\S]*?)```/i);
  if (fenced) candidates.push(fenced[1]);

  const commentBlock = source.match(/<!--\s*retro-meta-start\s*-->([\s\S]*?)<!--\s*retro-meta-end\s*-->/i);
  if (commentBlock) candidates.push(commentBlock[1]);
  const inlineComment = source.match(/<!--\s*retro-meta\s*([\s\S]*?)-->/i);
  if (inlineComment) candidates.push(inlineComment[1]);

  candidates.push(stripHtml(source));

  for (const rawCandidate of candidates) {
    const cleaned = stripHtml(rawCandidate);
    const variants = [rawCandidate, cleaned, jsonObjectCandidate(cleaned)].filter(Boolean);
    for (const variant of variants) {
      const parsed = parseJsonCandidate(variant);
      const meta = unwrapRetroMeta(parsed);
      if (meta) return meta;
    }
  }

  return null;
};

const normalizeRequirements = (requirements) => {
  if (!isObject(requirements)) return null;
  return {
    framework: uniqueStrings(requirements.framework || requirements.frameworks),
    dependencies: uniqueStrings(requirements.dependencies || requirements.dependency),
    fileSize: requirements.fileSize || requirements.size || null,
    version: requirements.version || null,
    lastUpdate: requirements.lastUpdate || requirements.updated || null,
    license: requirements.license || null
  };
};

const normalizeFeatures = (features) => {
  if (!Array.isArray(features)) return null;
  return features.map(feature => {
    if (typeof feature === 'string') return { title: '', items: [feature] };
    if (!isObject(feature)) return null;
    return {
      title: feature.title || feature.name || '',
      items: uniqueStrings(feature.items || feature.points || feature.features)
    };
  }).filter(feature => feature && (feature.title || feature.items.length));
};

const normalizeFaq = (faq) => {
  if (!Array.isArray(faq)) return null;
  return faq.map(item => {
    if (!isObject(item)) return null;
    return {
      question: item.question || item.q || '',
      answer: item.answer || item.a || ''
    };
  }).filter(item => item && item.question && item.answer);
};

const normalizeBundle = (bundle) => {
  if (!isObject(bundle)) return null;
  return {
    isBundle: booleanValue(bundle.isBundle, bundle.type === 'bundle'),
    key: bundle.key || bundle.slug || null,
    label: bundle.label || bundle.name || null,
    tebexPackageId: Number(bundle.tebexPackageId || bundle.packageId || bundle.id) || null,
    items: ensureArray(bundle.items || bundle.products || bundle.packageIds)
      .map(item => Number(isObject(item) ? (item.id || item.packageId || item.tebexPackageId) : item))
      .filter(Number.isFinite)
  };
};

const normalizeBundleMemberships = (bundles) => ensureArray(bundles).map(bundle => {
  if (typeof bundle === 'number' || typeof bundle === 'string') {
    const id = Number(bundle);
    return Number.isFinite(id) ? { id, key: null, label: null } : null;
  }
  if (!isObject(bundle)) return null;
  const id = Number(bundle.id || bundle.packageId || bundle.tebexPackageId);
  return {
    id: Number.isFinite(id) ? id : null,
    key: bundle.key || bundle.slug || null,
    label: bundle.label || bundle.name || null
  };
}).filter(bundle => bundle && (bundle.id || bundle.key || bundle.label));

/** Normalize both legacy metadata and schemaVersion 2 into one stable shape. */
export const normalizeRetroMeta = (meta) => {
  const source = unwrapRetroMeta(meta);
  if (!source) return null;

  const routingSource = isObject(source.routing) ? source.routing : {};
  const presentationSource = isObject(source.presentation) ? source.presentation : {};
  const seoSource = isObject(source.seo) ? source.seo : {};
  const overviewSource = isObject(source.overview) ? source.overview : {};
  const mediaSource = isObject(source.media) ? source.media : {};
  const schemaVersion = Math.max(1, Number(source.schemaVersion || source.schema_version) || 1);
  const canonicalSlug = slugifyProductName(
    routingSource.canonicalSlug || routingSource.canonical_slug || source.canonicalSlug || source.slug
  );

  return {
    schemaVersion,
    label: source.label || source.title || null,
    status: source.status || null,
    routing: {
      canonicalSlug: canonicalSlug || null,
      aliases: uniqueStrings(routingSource.aliases || source.aliases)
        .map(slugifyProductName)
        .filter(alias => alias && alias !== canonicalSlug)
    },
    presentation: {
      layout: presentationSource.layout || source.layout || 'editorial',
      theme: presentationSource.theme || presentationSource.themePreset || source.theme || null,
      eyebrow: presentationSource.eyebrow || source.eyebrow || source.hero?.eyebrow || null
    },
    seo: {
      title: seoSource.title || null,
      description: seoSource.description || null,
      image: seoSource.image || null,
      imageKey: seoSource.imageKey || seoSource.image_key || null
    },
    overview: {
      intro: overviewSource.intro || overviewSource.summary || null,
      body: overviewSource.body || overviewSource.description || null,
      highlights: uniqueStrings(overviewSource.highlights),
      closing: overviewSource.closing || null
    },
    features: normalizeFeatures(source.features),
    requirements: normalizeRequirements(source.requirements),
    faq: normalizeFaq(source.faq),
    media: {
      youtube: mediaSource.youtube || mediaSource.youtubeId || mediaSource.video || source.youtube || null,
      images: ensureArray(mediaSource.images),
      videos: ensureArray(mediaSource.videos)
    },
    tags: uniqueStrings(source.tags),
    license: source.license || null,
    youtube: source.youtube || null,
    category: source.category || null,
    script_name: source.script_name || source.scriptName || source.changelogScriptName || null,
    changelogScriptName: source.changelogScriptName || source.scriptName || source.script_name || null,
    topSeller: booleanValue(source.topSeller, booleanValue(source.top_seller)),
    bundle: normalizeBundle(source.bundle),
    bundles: normalizeBundleMemberships(source.bundles)
  };
};

export const parseRetroMeta = description => normalizeRetroMeta(extractRetroMeta(description));

export const isTebexImageUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  try {
    return new URL(url).hostname.toLowerCase() === TEBEX_MEDIA_HOST;
  } catch (error) {
    return false;
  }
};

const normalizeBackendUrl = value => String(value || DEFAULT_BACKEND_URL).replace(/\/+$/, '');

export const optimizeTebexImageUrl = (url, width = 960, backendUrl = DEFAULT_BACKEND_URL) => {
  if (!isTebexImageUrl(url)) return url;
  const requestedWidth = Number(width);
  const selectedWidth = TEBEX_IMAGE_WIDTHS.includes(requestedWidth) ? requestedWidth : 960;
  return `${normalizeBackendUrl(backendUrl)}/api/tebex/image?src=${encodeURIComponent(url)}&w=${selectedWidth}`;
};

export const buildResponsiveImageSources = (
  url,
  { backendUrl = DEFAULT_BACKEND_URL, widths = [640, 1280, 1920], nativeWidth = null } = {}
) => {
  if (!url) return [];
  const maximumWidth = Number(nativeWidth) || Infinity;
  return uniqueStrings(widths)
    .map(Number)
    .filter(width => TEBEX_IMAGE_WIDTHS.includes(width) && width <= maximumWidth)
    .sort((left, right) => left - right)
    .map(width => ({ src: optimizeTebexImageUrl(url, width, backendUrl), width }));
};

const mediaKey = (item) => {
  if (!item) return null;
  if (item.type === 'video') {
    const youtubeId = item.youtubeId || extractYouTubeId(item.url || item.src);
    return youtubeId ? `youtube:${youtubeId}` : `video:${item.url || item.src || ''}`;
  }
  const source = item.originalSrc || item.url || item.src;
  if (!source) return null;
  try {
    const parsed = new URL(source, 'https://retrostore.dev');
    return `image:${parsed.pathname.replace(/\/+$/, '')}`.toLowerCase();
  } catch (error) {
    return `image:${String(source).split(/[?#]/, 1)[0]}`.toLowerCase();
  }
};

const normalizeImageMedia = (value, index, title, backendUrl) => {
  const source = typeof value === 'string' ? value : (value?.src || value?.url);
  if (!source) return null;
  const remoteTebexImage = isTebexImageUrl(source);
  const width = typeof value === 'object' ? Number(value.width) || null : null;
  const srcSet = typeof value === 'object' && Array.isArray(value.srcSet)
    ? value.srcSet
    : (remoteTebexImage ? buildResponsiveImageSources(source, { backendUrl, nativeWidth: width }) : []);

  return {
    ...(typeof value === 'object' ? value : {}),
    type: 'image',
    src: remoteTebexImage ? optimizeTebexImageUrl(source, 1280, backendUrl) : source,
    originalSrc: remoteTebexImage ? source : (value?.originalSrc || null),
    srcSet,
    width,
    height: typeof value === 'object' ? Number(value.height) || null : null,
    alt: (typeof value === 'object' && value.alt) || `${title || 'Product'} screenshot ${index + 1}`,
    caption: (typeof value === 'object' && value.caption) || null
  };
};

const normalizeVideoMedia = (value, title) => {
  const source = typeof value === 'string' ? value : (value?.youtubeId || value?.url || value?.src);
  if (!source) return null;
  const youtubeId = extractYouTubeId(source);
  return {
    ...(typeof value === 'object' ? value : {}),
    type: 'video',
    youtubeId: youtubeId || null,
    url: youtubeId ? `https://www.youtube.com/watch?v=${youtubeId}` : source,
    alt: (typeof value === 'object' && value.alt) || `Play the ${title || 'product'} video`,
    caption: (typeof value === 'object' && value.caption) || null
  };
};

/**
 * Merge (rather than choose between) Tebex, retroMeta, and locally supplied
 * media. Native Tebex `primary` controls the first image; `featured` is not a
 * package-media ordering field.
 */
export const mergeEditorialMedia = ({
  packageMedia = [],
  metaMedia = null,
  extraMedia = [],
  fallbackImage = null,
  title = 'Product',
  backendUrl = DEFAULT_BACKEND_URL
} = {}) => {
  const stablePackageMedia = ensureArray(packageMedia)
    .map((item, index) => ({ item, index }))
    .sort((left, right) => {
      const primaryDifference = Number(booleanValue(right.item?.primary)) - Number(booleanValue(left.item?.primary));
      return primaryDifference || left.index - right.index;
    })
    .map(entry => entry.item);

  const packageImages = stablePackageMedia.filter(item => item?.type === 'image' && (item.url || item.src));
  const packageVideos = stablePackageMedia.filter(item => item?.type === 'video' && (item.url || item.src || item.youtubeId));
  const metadata = isObject(metaMedia) ? metaMedia : {};
  const metaImages = ensureArray(metadata.images);
  const metaVideos = [metadata.youtube, ...ensureArray(metadata.videos)].filter(Boolean);
  const extras = ensureArray(extraMedia);
  const extraImages = extras.filter(item => (typeof item === 'string') || !item.type || item.type === 'image');
  const extraVideos = extras.filter(item => item?.type === 'video');
  const imageInputs = [...packageImages, ...metaImages, ...extraImages];
  if (imageInputs.length === 0 && fallbackImage) imageInputs.push(fallbackImage);

  const output = [];
  const seen = new Set();
  imageInputs.forEach((item, index) => {
    const normalized = normalizeImageMedia(item, index, title, backendUrl);
    const key = mediaKey(normalized);
    if (normalized && key && !seen.has(key)) {
      seen.add(key);
      output.push(normalized);
    }
  });

  [...packageVideos, ...metaVideos, ...extraVideos].forEach(item => {
    const normalized = normalizeVideoMedia(item, title);
    const key = mediaKey(normalized);
    if (normalized && key && !seen.has(key)) {
      seen.add(key);
      output.push(normalized);
    }
  });

  return output;
};

export const normalizeCommerce = (pkg, catalogMeta = {}) => {
  const expiresAt = pkg?.expiration_date || pkg?.expires_at || null;
  const expirationTime = expiresAt ? Date.parse(expiresAt) : null;
  const expired = Number.isFinite(expirationTime) && expirationTime <= Date.now();
  const listed = !booleanValue(pkg?.disabled)
    && !booleanValue(pkg?.hidden)
    && pkg?.available !== false
    && pkg?.active !== false
    && !expired;
  const stale = booleanValue(catalogMeta?.stale, booleanValue(pkg?.catalogStale));
  const fresh = !stale;
  const disableGifting = booleanValue(pkg?.disable_gifting ?? pkg?.disableGifting);
  const disableQuantity = booleanValue(pkg?.disable_quantity ?? pkg?.disableQuantity);
  const amount = Number(pkg?.base_price ?? pkg?.price ?? 0);
  const totalAmount = Number(pkg?.total_price ?? amount);

  return {
    available: listed,
    purchasable: listed && fresh,
    fresh,
    stale,
    giftingEnabled: !disableGifting,
    quantityAdjustable: !disableQuantity,
    disableGifting,
    disableQuantity,
    userLimit: Number(pkg?.user_limit) || null,
    expiresAt,
    type: pkg?.type || null,
    price: {
      amount: Number.isFinite(amount) ? amount : 0,
      totalAmount: Number.isFinite(totalAmount) ? totalAmount : (Number.isFinite(amount) ? amount : 0),
      currency: pkg?.currency || 'USD',
      discount: pkg?.discount || null
    }
  };
};

const resolveSeoImageByKey = (images, imageKey) => {
  const normalizedKey = String(imageKey || '').trim();
  if (!normalizedKey) return null;

  const tebexImages = images.filter(item => isTebexImageUrl(item.originalSrc || item.src));
  if (normalizedKey.toLowerCase() === 'tebex:primary') {
    return tebexImages.find(item => booleanValue(item.primary)) || tebexImages[0] || images[0] || null;
  }

  const indexedTebexImage = normalizedKey.match(/^tebex:(\d+)$/i);
  if (indexedTebexImage) return tebexImages[Number(indexedTebexImage[1]) - 1] || null;

  return images.find(item => (
    [item.key, item.id, item.name, item.slug]
      .some(value => value !== null && value !== undefined && String(value) === normalizedKey)
  )) || null;
};

/**
 * Build the complete editorial page definition directly from a Tebex package.
 * `defaults` is intentionally a shallow compatibility/override layer for
 * local-only presentation data while products migrate to retroMeta v2.
 */
export const buildEditorialDefinition = (pkg, {
  extraMedia = [],
  defaults = {},
  meta: suppliedMeta = null,
  catalogMeta = {},
  backendUrl = DEFAULT_BACKEND_URL
} = {}) => {
  if (!pkg || typeof pkg !== 'object') return null;
  const meta = normalizeRetroMeta(suppliedMeta)
    || parseRetroMeta(pkg.description)
    || normalizeRetroMeta(defaults.meta)
    || normalizeRetroMeta({});
  const title = pkg.name || meta.label || defaults.title || 'Retro Store Product';
  const canonicalSlug = meta.routing.canonicalSlug
    || slugifyProductName(pkg.slug)
    || slugifyProductName(title)
    || String(pkg.id || defaults.id || 'product');
  const aliases = uniqueStrings([
    ...meta.routing.aliases,
    ...ensureArray(defaults.aliases)
  ]).map(slugifyProductName).filter(alias => alias && alias !== canonicalSlug);
  const commerce = normalizeCommerce(pkg, catalogMeta);
  const media = mergeEditorialMedia({
    packageMedia: pkg.media,
    metaMedia: meta.media,
    extraMedia,
    fallbackImage: pkg.image || defaults.image,
    title,
    backendUrl
  });
  const images = media.filter(item => item.type === 'image');
  const overview = {
    intro: meta.overview.intro || defaults.overview?.intro || null,
    body: meta.overview.body || defaults.overview?.body || meta.overview.intro || null,
    highlights: meta.overview.highlights.length
      ? meta.overview.highlights
      : ensureArray(defaults.overview?.highlights),
    closing: meta.overview.closing || defaults.overview?.closing || null
  };
  const presentationTheme = meta.presentation.theme || defaults.presentation?.theme || defaults.theme || 'default';
  const theme = isObject(presentationTheme)
    ? presentationTheme
    : (isObject(defaults.theme) ? defaults.theme : { name: presentationTheme });
  const summary = overview.intro || overview.body || defaults.hero?.summary || '';
  const seoDescription = meta.seo.description || defaults.seo?.description || summary;
  const seoImageKey = meta.seo.imageKey || defaults.seo?.imageKey || null;
  const keyedSeoImage = resolveSeoImageByKey(images, seoImageKey);
  const packageId = Number(pkg.id || pkg.tebexPackageId || defaults.tebexPackageId || defaults.id);

  return {
    schemaVersion: meta.schemaVersion,
    id: Number.isFinite(packageId) ? packageId : (pkg.id || defaults.id),
    tebexPackageId: Number.isFinite(packageId) ? packageId : (pkg.id || defaults.tebexPackageId),
    slug: canonicalSlug,
    aliases,
    routing: { canonicalSlug, aliases },
    status: meta.status || defaults.status || (commerce.available ? 'available' : 'unavailable'),
    title,
    category: meta.category || pkg.categoryName || pkg.category?.name || defaults.category || 'Script',
    tags: meta.tags.length ? meta.tags : ensureArray(defaults.tags),
    topSeller: meta.topSeller || defaults.topSeller === true,
    presentation: {
      layout: meta.presentation.layout || defaults.presentation?.layout || 'editorial',
      theme: presentationTheme,
      eyebrow: meta.presentation.eyebrow || defaults.presentation?.eyebrow || defaults.hero?.eyebrow || null
    },
    theme,
    seo: {
      title: meta.seo.title || defaults.seo?.title || `${title} | Retro Store`,
      description: seoDescription,
      imageKey: seoImageKey,
      image: meta.seo.image || defaults.seo?.image || keyedSeoImage?.src || images[0]?.src || null
    },
    hero: {
      eyebrow: meta.presentation.eyebrow || defaults.hero?.eyebrow || null,
      title,
      summary
    },
    overview,
    media,
    features: meta.features || defaults.features || [],
    requirements: meta.requirements || defaults.requirements || null,
    faq: meta.faq || defaults.faq || [],
    changelogScriptName: meta.changelogScriptName || defaults.changelogScriptName || null,
    bundle: defaults.bundle || meta.bundle,
    bundleMemberships: meta.bundles,
    commerce,
    meta
  };
};
